// Centralized audio management for better browser policy compliance

class AudioManager {
  private audioContext: AudioContext | null = null;
  private isInitialized: boolean = false;
  private pendingNotifications: (() => void)[] = [];

  async initialize(): Promise<boolean> {
    if (this.isInitialized) return true;

    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      this.isInitialized = true;
      console.log('🔊 AudioManager initialized successfully');

      // Play any pending notifications
      this.pendingNotifications.forEach(fn => fn());
      this.pendingNotifications = [];

      return true;
    } catch (error) {
      console.error('Failed to initialize AudioManager:', error);
      return false;
    }
  }

  async playNotificationSound(type: 'message' | 'groupMessage' = 'message'): Promise<void> {
    if (!this.isInitialized || !this.audioContext) {
      // Queue the notification to play when initialized
      this.pendingNotifications.push(() => this.playNotificationSound(type));

      // Try to initialize (might work if user has interacted)
      this.initialize();
      return;
    }

    try {
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      if (type === 'groupMessage') {
        // Double beep for group messages
        oscillator.frequency.setValueAtTime(1000, this.audioContext.currentTime);
        oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime + 0.1);
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.2, this.audioContext.currentTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.15);
        gainNode.gain.linearRampToValueAtTime(0.2, this.audioContext.currentTime + 0.2);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.35);

        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + 0.4);
      } else {
        // Single beep for regular messages
        oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.3, this.audioContext.currentTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.2);

        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + 0.2);
      }

      console.log(`🔊 ${type} notification sound played`);
    } catch (error) {
      console.error('Failed to play notification sound:', error);
    }
  }

  // Method to test if audio is working
  async testAudio(): Promise<boolean> {
    try {
      await this.playNotificationSound('message');
      return true;
    } catch {
      return false;
    }
  }
}

// Create singleton instance
export const audioManager = new AudioManager();

// Auto-initialize on user interaction
const initializeOnInteraction = () => {
  audioManager.initialize();
};

// Listen for various user interaction events
if (typeof window !== 'undefined') {
  const events = ['click', 'touchstart', 'keydown', 'touchend', 'mousedown'];

  const initOnce = () => {
    audioManager.initialize();
    // Remove listeners after first successful init
    events.forEach(event => {
      document.removeEventListener(event, initOnce);
    });
  };

  events.forEach(event => {
    document.addEventListener(event, initOnce, { passive: true });
  });
}